import React, { useState } from 'react';
import { Wheel } from 'react-custom-roulette';
import { Plus, RotateCcw } from 'lucide-react';
import ToolDescription from './ToolDescription';

const Roulette: React.FC = () => {
  const [items, setItems] = useState<string[]>([
    'Cyclone Griffins',
    'Efficient Sentinals',
    'Ultimate Knights',
    'The Unstoppable Force',
    'The Immortal Guardians',
    'Eternal Defenders'
  ]);
  const [newItem, setNewItem] = useState('');
  const [bulkItems, setBulkItems] = useState('');
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [winner, setWinner] = useState<string | null>(null);

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItem.trim()) {
      setItems([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleAddBulkItems = (e: React.FormEvent) => {
    e.preventDefault();
    if (bulkItems.trim()) {
      const newItems = bulkItems
        .split('\n')
        .map(item => item.trim())
        .filter(item => item !== '');
      setItems([...items, ...newItems]);
      setBulkItems('');
    }
  };

  const handleSpin = () => {
    if (items.length > 0 && !mustSpin) {
      const newPrizeNumber = Math.floor(Math.random() * items.length);
      setPrizeNumber(newPrizeNumber);
      setMustSpin(true);
      setWinner(null);
    }
  };

  const handleStopSpinning = () => {
    setMustSpin(false);
    setWinner(items[prizeNumber]);
  };

  const clearItems = () => {
    setItems([]);
    setWinner(null);
  };

  return (
    <div className="space-y-6">
      <ToolDescription 
        title="Teams Spinner"
        description="Make fair, random team selections and decisions with this interactive spinning wheel. Perfect for assigning tasks, choosing presentation order, or making any team-related decisions in a fun and transparent way."
        benefits={[
          "Ensure fair and unbiased team selections",
          "Add an element of fun to team decisions",
          "Maintain transparency in selection processes",
          "Reduce decision fatigue in team meetings",
          "Create engagement and anticipation"
        ]}
        howToUse="Add your team names, options, or choices to the spinner wheel. Click spin to make a random selection. You can customize the list by adding or removing items as needed. Great for choosing who presents first, task assignments, or team rotations."
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
          Teams Spinner
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <form onSubmit={handleAddItem} className="flex gap-2">
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                placeholder="Add single item..."
                className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Add
              </button>
            </form>

            <form onSubmit={handleAddBulkItems} className="space-y-2">
              <textarea
                value={bulkItems}
                onChange={(e) => setBulkItems(e.target.value)}
                placeholder="Paste multiple items here (one per line)..."
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white min-h-[120px] resize-y"
                rows={5}
              />
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Multiple Items
              </button>
            </form>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-white">Added Items</h3>
                <button
                  onClick={clearItems}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                >
                  <RotateCcw size={16} />
                  Clear All
                </button>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm flex justify-between items-center"
                  >
                    <span className="text-gray-800 dark:text-white">{item}</span>
                    <button
                      onClick={() => setItems(items.filter((_, i) => i !== index))}
                      className="text-red-500 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center space-y-4">
            {items.length > 0 ? (
              <>
                <div className="relative">
                  <Wheel
                    mustStartSpinning={mustSpin}
                    prizeNumber={prizeNumber}
                    data={items.map(item => ({ option: item }))}
                    onStopSpinning={handleStopSpinning}
                    backgroundColors={['#ff8f43', '#70bd9b', '#890f0d', '#183652', '#ff8f43']}
                    textColors={['#ffffff']}
                    outerBorderColor="#f2f2f2"
                    outerBorderWidth={2}
                    innerRadius={0}
                    radiusLineColor="#dddddd"
                    radiusLineWidth={1}
                    fontSize={16}
                    spinDuration={0.8}
                  />
                </div>
                <button
                  onClick={handleSpin}
                  disabled={mustSpin}
                  className={`
                    bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg
                    ${mustSpin
                      ? 'cursor-not-allowed'
                      : ''}
                  `}
                >
                  SPIN!
                </button>
                {winner && (
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                      Winner:
                    </h3>
                    <p className="text-2xl text-blue-500 font-bold mt-2">
                      {winner}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400">
                Add items to start the roulette
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roulette;